import {
  DOMAINE_ORDER,
  DOMAINE_VITRINE,
  type DomaineVitrineMeta,
} from '../constants/academiaFiliereDomains';

export type PublicDomaine = {
  code: string;
  libelle: string;
  slug: string;
  intro?: string | null;
  urlImage?: string | null;
  icon?: string;
  ordre?: number;
  estActive?: boolean;
};

export type PublicFiliere = {
  id: string;
  code: string;
  libelle: string;
  description?: string | null;
  urlImage?: string | null;
  domaine: string;
  estActive?: boolean;
  ordre?: number;
  contenuPedagogique?: unknown;
};

export type PublicFiliereTrack = DomaineVitrineMeta & {
  filieres: PublicFiliere[];
};

function metaForDomaineKey(key: string, domaines?: PublicDomaine[]): DomaineVitrineMeta {
  const fromApi = domaines?.find((d) => d.code.toUpperCase() === key);
  if (fromApi) {
    // Priorité au texte descriptif admin ; jamais le fallback vitrine si le domaine existe en base.
    const intro = fromApi.intro?.trim() ?? '';
    return {
      domaineKey: fromApi.code.toUpperCase(),
      slug: fromApi.slug,
      title: fromApi.libelle,
      intro,
      urlImage: fromApi.urlImage ?? null,
      icon: fromApi.icon ?? 'school',
    };
  }
  return (
    DOMAINE_VITRINE[key] ?? {
      domaineKey: key,
      slug: key.toLowerCase().replace(/_/g, '-'),
      title: key.replace(/_/g, ' '),
      intro: 'Formations du centre JCONSULT MY Academia.',
      icon: 'school',
    }
  );
}

export function groupFilieresForPublicVitrine(
  filieres: PublicFiliere[],
  domaines?: PublicDomaine[],
): PublicFiliereTrack[] {
  const active = filieres.filter((f) => f.estActive !== false);
  const map = new Map<string, PublicFiliere[]>();

  for (const f of active) {
    const key = (f.domaine ?? 'AUTRE').toUpperCase();
    const list = map.get(key) ?? [];
    list.push(f);
    map.set(key, list);
  }

  const orderKeys = domaines?.length
    ? [...domaines]
        .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0) || a.libelle.localeCompare(b.libelle, 'fr'))
        .map((d) => d.code.toUpperCase())
    : [...DOMAINE_ORDER];

  const tracks: PublicFiliereTrack[] = [];
  const seen = new Set<string>();

  for (const key of orderKeys) {
    const list = map.get(key);
    if (!list?.length) continue;
    seen.add(key);
    list.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0) || a.libelle.localeCompare(b.libelle, 'fr'));
    tracks.push({ ...metaForDomaineKey(key, domaines), filieres: list });
  }

  const otherKeys = Array.from(map.keys()).filter((k) => !seen.has(k));
  for (const key of otherKeys.sort()) {
    const list = map.get(key);
    if (!list?.length) continue;
    list.sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0) || a.libelle.localeCompare(b.libelle, 'fr'));
    tracks.push({ ...metaForDomaineKey(key, domaines), filieres: list });
  }

  return tracks;
}

export function findPublicFiliereByCode(
  filieres: PublicFiliere[],
  code: string | null | undefined,
): PublicFiliere | undefined {
  if (!code) return undefined;
  const normalized = code.trim().toUpperCase();
  return filieres.find((f) => f.code.toUpperCase() === normalized);
}

export function slugifyFiliereLibelle(libelle: string): string {
  return libelle
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

/** Compat. ancien paramètre `?cours=slug-libelle`. */
export function findPublicFiliereByCoursSlug(
  filieres: PublicFiliere[],
  coursSlug: string | null | undefined,
): PublicFiliere | undefined {
  if (!coursSlug) return undefined;
  const s = coursSlug.toLowerCase();
  return filieres.find((f) => slugifyFiliereLibelle(f.libelle) === s);
}

/** URL publique d’une formation dans son domaine (`/filieres/:domaine/:formation`). */
export function buildPublicFiliereDetailPath(
  trackSlug: string,
  filiere: Pick<PublicFiliere, 'libelle'>,
): string {
  return `/filieres/${encodeURIComponent(trackSlug)}/${slugifyFiliereLibelle(filiere.libelle)}`;
}
